import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-up">
        <img src="/studio-castelli-logo.jpg" alt="Studio Castelli" className="w-44 h-auto mb-10" />

        {done ? (
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-navy mb-2">Controlla la tua email</h2>
            <p className="text-sm text-muted-foreground" data-testid="forgot-password-confirmation">
              Se l'indirizzo è registrato, riceverai a breve un link per reimpostare la password.
              Il link scade tra 1 ora.
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Password dimenticata</h2>
            <p className="text-muted-foreground text-sm mb-8">
              Inserisci la tua email: ti invieremo un link per reimpostare la password.
            </p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@esempio.it"
                    className="pl-10 h-11"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>
              {error && (
                <div data-testid="forgot-password-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-button"
                className="w-full h-11 bg-navy hover:bg-navy-light text-white font-semibold transition-colors duration-200"
              >
                {loading ? "Invio in corso..." : "Invia link di reimpostazione"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-flex items-center gap-2 text-sm text-navy/70 hover:text-gold transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
